'use client';

import React from 'react';
import { Select, SelectItem } from '@heroui/react';
import { FilterSelectProps } from './types';
import { SortArrowDown, SortArrowUp } from '../icons';

export type { FilterOption } from './types';

const FilterSelect: React.FC<FilterSelectProps> = ({
  placeholder,
  options,
  selectedKey,
  onSelectionChange,
  startContent,
  endContent,
  className = '',
  disabled = false,
  size = 'sm',
  variant = 'default',
  autoWidth = false,
  sortDirection = null,
  onSortDirectionChange,
  ariaLabel,
  showPlaceholderWithValue = false,
  popoverWidth,
}) => {
  // Check if nothing is selected (show placeholder)
  // Empty string '' can be a valid key (e.g., "All campaigns" reset option)
  const isPlaceholderSelected = React.useMemo(() => {
    if (!options || options.length === 0) {
      return true;
    }
    // Check if selectedKey exists in options (including empty string)
    const hasSelectedInOptions = options.some((option) => option.key === selectedKey);
    return !hasSelectedInOptions;
  }, [options, selectedKey]);

  const getSelectedOptionWithIcon = () => {
    const selectedOption = options.find((option) => option.key === selectedKey);
    if (!selectedOption) return placeholder;

    // Проверяем, является ли это сортировочным фильтром
    const isSortFilter = options.some(
      (option) =>
        option.key !== 'all' &&
        (option.key.startsWith('-') ||
          option.key.includes('created_at') ||
          option.key.includes('arpu') ||
          option.key.includes('romi'))
    );

    const getSortDirection = (key: string) => {
      if (key === 'all' || !isSortFilter) return null;
      if (key.startsWith('-')) return 'desc';
      return 'asc';
    };

    const direction = getSortDirection(selectedOption.key);

    // Если включен режим showPlaceholderWithValue и выбран 'all', показываем только placeholder серым цветом
    if (showPlaceholderWithValue && selectedOption.key === 'all') {
      return <span className="text-auth-muted">{placeholder}</span>;
    }

    // Если включен режим showPlaceholderWithValue и выбран конкретный элемент (не placeholder и не 'all')
    if (showPlaceholderWithValue && !isPlaceholderSelected && selectedOption.key !== 'all') {
      return (
        <div className="flex items-center gap-1">
          <span className="text-auth-muted">{placeholder}:</span>
          <span className="text-auth-ink">{selectedOption.label}</span>
        </div>
      );
    }

    if (direction && selectedOption.key !== 'all' && isSortFilter) {
      return (
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            {selectedOption.icon && <span className="flex-shrink-0">{selectedOption.icon}</span>}
            <span>{selectedOption.label}</span>
          </div>
          <span className="ml-2">{direction === 'asc' ? '↑' : '↓'}</span>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2">
        {selectedOption.icon && <span>{selectedOption.icon}</span>}
        <span>{selectedOption.label}</span>
      </div>
    );
  };

  const getVariantStyles = () => {
    switch (variant) {
      case 'campaign':
        return {
          trigger: 'bg-auth-surface border-none rounded-2 !px-3 !min-h-9 !h-9',
          value: 'text-sm',
        };
      default:
        return {
          trigger:
            'h-9 bg-gray-100 border-none rounded-2 !px-3 hover:bg-gray-200 focus:border-blue-500',
          value: 'text-sm',
        };
    }
  };

  const variantStyles = getVariantStyles();
  const valueColorClass = isPlaceholderSelected ? '!text-auth-muted' : '!text-black';
  const hasSelectedInOptions = React.useMemo(
    () => options.some((option) => option.key === selectedKey),
    [options, selectedKey]
  );
  const resolvedSelectedKeys = hasSelectedInOptions ? [selectedKey] : [];

  return (
    <div
      className="w-fit filter-select-wrapper"
      style={{
        textOverflow: 'clip',
        whiteSpace: 'nowrap',
        overflow: 'visible',
      }}
    >
      <Select
        placeholder={placeholder}
        aria-label={ariaLabel || placeholder}
        selectedKeys={resolvedSelectedKeys}
        onSelectionChange={(keys) => {
          const selected = Array.from(keys)[0] as string;
          // Предотвращаем снятие выбора - если keys пустой, игнорируем изменение
          // Но пустая строка '' - валидный ключ для сброса фильтра
          if (selected === undefined || keys.size === 0) {
            return;
          }
          onSelectionChange(selected);
        }}
        selectorIcon={<span aria-hidden className="hidden" />}
        disallowEmptySelection
        renderValue={() => getSelectedOptionWithIcon()}
        startContent={startContent}
        disabled={disabled}
        size={size}
        style={{
          minWidth: 'fit-content',
          width: 'fit-content',
          textOverflow: 'clip',
        }}
        classNames={{
          base: 'w-fit min-w-fit',
          trigger: `${variantStyles.trigger} ${
            disabled ? 'opacity-50 cursor-not-allowed' : ''
          } w-fit min-w-fit whitespace-nowrap ${className}`,
          value: `${variantStyles.value} ${valueColorClass} whitespace-nowrap min-w-fit overflow-visible`,
          popoverContent: `rounded-xl w-fit ${popoverWidth ? '' : 'max-w-[13.0625rem]'}`,
          listbox: 'p-2',
          listboxWrapper: popoverWidth ? '' : ' max-w-[13.0625rem]',
          innerWrapper: 'gap-1 min-w-fit flex-nowrap',
        }}
        popoverProps={{
          classNames: {
            content: popoverWidth
              ? `w-[${popoverWidth}px] p-0`
              : 'w-[11rem] max-w-[13.0625rem] p-0 ',
          },
          style: popoverWidth
            ? { width: `${popoverWidth}px`, maxWidth: `${popoverWidth}px` }
            : undefined,
        }}
      >
        {options.map((option) => {
          // Проверяем, является ли это сортировочным фильтром
          const isSortFilter = options.some(
            (opt) =>
              opt.key !== 'all' &&
              (opt.key.startsWith('-') ||
                opt.key.includes('created_at') ||
                opt.key.includes('arpu') ||
                opt.key.includes('romi'))
          );

          const getSortDirection = () => {
            if (option.key === 'all' || !isSortFilter) return null;
            if (option.key.startsWith('-')) return 'desc';
            return 'asc';
          };
          const direction = getSortDirection();
          return (
            <SelectItem
              key={option.key}
              textValue={option.label}
              selectedIcon={
                direction && option.key !== 'all' && isSortFilter ? (
                  direction === 'asc' ? (
                    <SortArrowUp />
                  ) : (
                    <SortArrowDown />
                  )
                ) : null
              }
              classNames={{
                base: '!p-2 !h-8 whitespace-nowrap !w-full !data-[hover=true]:bg-[#EEEEEF]',
              }}
            >
              <div className="flex items-center gap-2">
                {option.icon && <span className="flex-shrink-0">{option.icon}</span>}
                <span>{option.label}</span>
              </div>
            </SelectItem>
          );
        })}
      </Select>
    </div>
  );
};

export default FilterSelect;
